import React, { useState, useMemo } from 'react';
import { authAxios } from '@/lib/api';
import { logError } from '@/lib/logger';
import { STAGES, todayISO, isOverdue } from './crm/crmConstants';
import { OverdueAlert, PipelineSummary, PriorityFilter } from './crm/CRMFilters';
import InquiryList from './crm/InquiryList';
import InquiryDetail from './crm/InquiryDetail';

export default function InquiryCRM({ inquiries, setInquiries }) {
  const [selectedId, setSelectedId] = useState(null);
  const [stageFilter, setStageFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const today = todayISO();

  const overdueCount = useMemo(() => inquiries.filter(i => isOverdue(i, today)).length, [inquiries, today]);

  const filtered = useMemo(() => inquiries.filter(i =>
    (stageFilter === 'all' || (i.stage || 'new') === stageFilter) &&
    (priorityFilter === 'all' || (i.priority || '') === priorityFilter)
  ), [inquiries, stageFilter, priorityFilter]);

  const stageCounts = useMemo(() => {
    const counts = Object.fromEntries(STAGES.map(s => [s.key, 0]));
    inquiries.forEach(i => { const stage = i.stage || 'new'; if (stage in counts) counts[stage] += 1; });
    return counts;
  }, [inquiries]);

  const selectedInquiry = inquiries.find(i => i.id === selectedId) || null;

  const mergeInquiry = (id, changes) =>
    setInquiries(prev => prev.map(i => (i.id === id ? { ...i, ...changes(i) } : i)));

  const updateInquiry = async (id, data) => {
    try {
      const res = await authAxios.put(`/api/inquiries/${id}`, data);
      mergeInquiry(id, () => res.data);
    } catch (e) { logError('Update failed:', e); }
  };

  const addNote = async (id, text) => {
    const res = await authAxios.post(`/api/inquiries/${id}/notes`, { text });
    mergeInquiry(id, i => ({ notes: [...(i.notes || []), res.data] }));
  };

  const deleteNote = async (id, noteId) => {
    try {
      await authAxios.delete(`/api/inquiries/${id}/notes/${noteId}`);
      mergeInquiry(id, i => ({ notes: (i.notes || []).filter(n => n.id !== noteId) }));
    } catch (e) { logError('Delete note failed:', e); }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    try {
      await authAxios.delete(`/api/inquiries/${id}`);
      setInquiries(prev => prev.filter(i => i.id !== id));
      setSelectedId(null);
    } catch (e) { logError('Delete failed:', e); }
  };

  return (
    <div data-testid="inquiry-crm">
      <OverdueAlert count={overdueCount} onViewAll={() => setStageFilter('all')} />
      <PipelineSummary total={inquiries.length} value={stageFilter} counts={stageCounts} onChange={setStageFilter} />
      <PriorityFilter value={priorityFilter} onChange={setPriorityFilter} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <InquiryList inquiries={filtered} today={today} selectedId={selectedInquiry?.id} stageFilter={stageFilter} onSelect={setSelectedId} />
        {selectedInquiry && (
          <InquiryDetail
            key={selectedInquiry.id}
            inquiry={selectedInquiry}
            today={today}
            onClose={() => setSelectedId(null)}
            onUpdate={updateInquiry}
            onAddNote={addNote}
            onDeleteNote={deleteNote}
            onDelete={deleteInquiry}
          />
        )}
      </div>
    </div>
  );
}
